import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  subtitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
};

function ChartTooltip({ active, payload, label, prefix, suffix }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        fontSize: '12px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '2px' }}>{label}</div>
      <div style={{ color: payload[0].color }}>
        {prefix}{payload[0].value.toFixed(2)}{suffix}
      </div>
    </div>
  );
}

export default function PriceArchitectureChart({ timeline }) {
  const { meanSpread, meanRatio } = useMemo(() => {
    if (!timeline || timeline.length === 0) return { meanSpread: 0, meanRatio: 0 };
    const spreads = timeline.map((t) => t.avgSpread).filter((v) => v != null);
    const ratios = timeline.map((t) => t.avgRatio).filter((v) => v != null);
    return {
      meanSpread: spreads.length > 0 ? spreads.reduce((a, b) => a + b, 0) / spreads.length : 0,
      meanRatio: ratios.length > 0 ? ratios.reduce((a, b) => a + b, 0) / ratios.length : 0,
    };
  }, [timeline]);

  if (!timeline || timeline.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Architecture</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No architecture data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Architecture</div>
      <div style={styles.grid}>
        <div>
          <div style={styles.subtitle}>Pest365+ Spread ($)</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeline} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip content={<ChartTooltip prefix="$" suffix="" />} />
              <ReferenceLine
                y={meanSpread}
                stroke="#9ca3af"
                strokeDasharray="4 3"
                strokeWidth={1}
                label={{
                  value: `Mean: $${meanSpread.toFixed(2)}`,
                  position: 'right',
                  fontSize: 10,
                  fill: '#9ca3af',
                }}
              />
              <Line
                type="monotone"
                dataKey="avgSpread"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div style={styles.subtitle}>Pest365+ Ratio (x)</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeline} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={false}
                axisLine={{ stroke: '#e5e7eb' }}
                tickFormatter={(v) => `${v}x`}
              />
              <Tooltip content={<ChartTooltip prefix="" suffix="x" />} />
              <ReferenceLine
                y={meanRatio}
                stroke="#9ca3af"
                strokeDasharray="4 3"
                strokeWidth={1}
                label={{
                  value: `Mean: ${meanRatio.toFixed(3)}x`,
                  position: 'right',
                  fontSize: 10,
                  fill: '#9ca3af',
                }}
              />
              <Line
                type="monotone"
                dataKey="avgRatio"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#2563eb', stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
